import React, { useEffect, useMemo, useRef, useState } from "react";
import { Images } from "../../../utils/imageConstants";
import BreadCrumb from "../../../components/BreadCrumb";
import { PlayerCard } from "../../../components/PlayerCard";
import { useQuery } from "@tanstack/react-query";
import { useAppSelector } from "../../../redux/hooks";
import { selectSelectedLeagueId } from "../../../redux/slice/selectedLeagueSlice";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { Api } from "../../../services/interceptor";
import type { IPlayerByLeague, ITeamWithTournament, IDivisionsResponse, IDivisionTeam } from "./types/squads";
import NoDataFound from "../../../components/common/NoDataFound";
import useDebounce from "../../../hooks/useDebounce";

const Squads: React.FC = () => {
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);
  
  // State for filters
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null);
  const [selectedTeamSnapshotId, setSelectedTeamSnapshotId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Debounced search query
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  
  // Dropdown states
  const [showTournamentDropdown, setShowTournamentDropdown] = useState(false);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [tournamentSearch, setTournamentSearch] = useState("");
  const [teamSearch, setTeamSearch] = useState("");
  
  // Refs for click outside
  const tournamentDropdownRef = useRef<HTMLDivElement>(null);
  const teamDropdownRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  // Fetch all teams (for tournament and team dropdowns)
  const { data: allTeams = [] } = useQuery<ITeamWithTournament[]>({
    queryKey: [QUERY_KEY.getAllTeam, selectedLeagueId],
    enabled: !!selectedLeagueId,
    queryFn: async () => {
      if (!selectedLeagueId) return [];
      const response = await Api.get<ITeamWithTournament[]>(
        `${QUERY_KEY.getAllTeam}/${selectedLeagueId}`
      );
      return Array.isArray(response?.data) ? response.data : [];
    },
  });

  // Get unique tournaments from allTeams
  const tournaments = useMemo(() => {
    const tournamentMap = new Map<string, { tournamentId: string; tournamentName: string }>();
    allTeams.forEach((team) => {
      if (team.tournament?.tournamentId && team.tournament?.tournamentName) {
        tournamentMap.set(team.tournament.tournamentId, {
          tournamentId: team.tournament.tournamentId,
          tournamentName: team.tournament.tournamentName,
        });
      }
    });
    return Array.from(tournamentMap.values());
  }, [allTeams]);

  // Filter tournaments by search
  const filteredTournaments = useMemo(
    () =>
      tournaments.filter((t) =>
        t.tournamentName.toLowerCase().includes(tournamentSearch.toLowerCase())
      ),
    [tournaments, tournamentSearch]
  );

  // Fetch divisions when tournament is selected
  const { data: divisionsData } = useQuery<IDivisionsResponse | null>({
    queryKey: [QUERY_KEY.getDivisions, selectedTournamentId],
    enabled: !!selectedTournamentId,
    queryFn: async () => {
      if (!selectedTournamentId) return null;
      const response = await Api.get<IDivisionsResponse>(
        `${QUERY_KEY.getDivisions}/${selectedTournamentId}`
      );
      return response?.data || null;
    },
  });

  // Get all teams from divisions (from childDivisions and teams arrays)
  const teamsFromDivisions = useMemo(() => {
    if (!divisionsData?.divisionResponseList) return [];
    const teams: IDivisionTeam[] = [];
    
    divisionsData.divisionResponseList.forEach((division: { teams?: IDivisionTeam[]; childDivisions?: Array<{ teams?: IDivisionTeam[] }> }) => {
      // Add teams from main division
      if (division.teams && Array.isArray(division.teams)) {
        teams.push(...division.teams);
      }
      
      // Add teams from child divisions
      if (division.childDivisions && Array.isArray(division.childDivisions)) {
        division.childDivisions.forEach((childDivision: { teams?: IDivisionTeam[] }) => {
          if (childDivision.teams && Array.isArray(childDivision.teams)) {
            teams.push(...childDivision.teams);
          }
        });
      }
    });
    
    return teams;
  }, [divisionsData]);

  // Get available teams: from divisions if tournament selected, otherwise from allTeams
  const availableTeams = useMemo(() => {
    if (selectedTournamentId && teamsFromDivisions.length > 0) {
      return teamsFromDivisions;
    }
    // Return teams from allTeams
    return allTeams.map((team) => ({
      teamSnapshotId: team.teamSnapshotId,
      teamName: team.teamName || team.team?.teamName || "",
      team: team.team,
    }));
  }, [selectedTournamentId, teamsFromDivisions, allTeams]);

  // Filter teams by search
  const filteredTeams = useMemo(
    () =>
      availableTeams.filter((t) =>
        (t.teamName || "").toLowerCase().includes(teamSearch.toLowerCase())
      ),
    [availableTeams, teamSearch]
  );

  // Get selected tournament name
  const selectedTournamentName = useMemo(() => {
    if (!selectedTournamentId) return "Select Tournament";
    const tournament = tournaments.find((t) => t.tournamentId === selectedTournamentId);
    return tournament?.tournamentName || "Select Tournament";
  }, [selectedTournamentId, tournaments]);

  // Get selected team name
  const selectedTeamName = useMemo(() => {
    if (!selectedTeamSnapshotId) return "All Teams";
    const team = availableTeams.find((t) => t.teamSnapshotId === selectedTeamSnapshotId);
    return team?.teamName || "All Teams";
  }, [selectedTeamSnapshotId, availableTeams]);

  // Fetch players based on filters
  // Priority:
  // 1. If team is selected: use playersByTeamSnapshotId (no search API)
  // 2. If search query exists and tournament is selected: use searchPlayersByNameForLeague with tournamentId
  // 3. If search query exists and no tournament: use searchPlayersByNameForLeague with leagueId
  // 4. If tournament is selected (no search): use getPlayersByTournament
  // 5. Otherwise: use searchPlayersByLeague
  const { data: players = [], isLoading } = useQuery<IPlayerByLeague[]>({
    queryKey: [
      selectedTeamSnapshotId
        ? QUERY_KEY.getAllPlayers
        : debouncedSearchQuery
        ? QUERY_KEY.searchPlayersByNameForLeague
        : selectedTournamentId
        ? QUERY_KEY.getPlayersByTournament
        : QUERY_KEY.getAllPlayersByLeague,
      selectedTeamSnapshotId || 
      (debouncedSearchQuery && selectedTournamentId ? `search-${debouncedSearchQuery}-tournament-${selectedTournamentId}` : null) ||
      (debouncedSearchQuery && !selectedTournamentId ? `search-${debouncedSearchQuery}-league-${selectedLeagueId}` : null) ||
      selectedTournamentId || 
      selectedLeagueId,
    ],
    enabled: !!selectedLeagueId,
    queryFn: async () => {
      if (!selectedLeagueId) return [];
      
      let response;
      if (selectedTeamSnapshotId) {
        // Fetch players by team (no search API for teams)
        response = await Api.get<IPlayerByLeague[]>(
          `${QUERY_KEY.getAllPlayers}/${selectedTeamSnapshotId}`
        );
      } else if (debouncedSearchQuery && selectedTournamentId) {
        // Search players by name for tournament
        response = await Api.get<IPlayerByLeague[]>(
          `${QUERY_KEY.searchPlayersByNameForLeague}?name=${encodeURIComponent(debouncedSearchQuery)}&tournamentId=${selectedTournamentId}`
        );
      } else if (debouncedSearchQuery && !selectedTournamentId) {
        // Search players by name for league (using leagueId)
        response = await Api.get<IPlayerByLeague[]>(
          `${QUERY_KEY.searchPlayersByNameForLeague}?name=${encodeURIComponent(debouncedSearchQuery)}&leagueId=${selectedLeagueId}`
        );
      } else if (selectedTournamentId) {
        // Fetch players by tournament
        response = await Api.get<IPlayerByLeague[]>(
          `${QUERY_KEY.getPlayersByTournament}?tournamentId=${selectedTournamentId}`
        );
      } else {
        // Fetch all players by league
        response = await Api.get<IPlayerByLeague[]>(
          `${QUERY_KEY.getAllPlayersByLeague}?leagueId=${selectedLeagueId}`
        );
      }
      
      return Array.isArray(response?.data) ? response.data : [];
    },
  });

  // Filter players by search query (only if team is selected, since team API doesn't support search)
  const filteredPlayers = useMemo(() => {
    // If team is selected, filter locally (team API doesn't support search)
    if (selectedTeamSnapshotId && searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return players.filter(
        (player) =>
          player.playerFirstName?.toLowerCase().includes(query) ||
          player.playerLastName?.toLowerCase().includes(query) ||
          `${player.playerFirstName} ${player.playerLastName}`.toLowerCase().includes(query)
      );
    }
    // Otherwise, return players as-is (search is handled by API)
    return players;
  }, [players, searchQuery, selectedTeamSnapshotId]);

  // Reset team when tournament changes
  useEffect(() => {
    setSelectedTeamSnapshotId(null);
  }, [selectedTournamentId]);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tournamentDropdownRef.current &&
        !tournamentDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTournamentDropdown(false);
      }
      if (
        teamDropdownRef.current &&
        !teamDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTeamDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="main-middle-content-wrap">
      <div className="squads-wrapper custom-container">
        <BreadCrumb />

        <div className="topbar">
          <h1 className="heading">Squads</h1>
          <div className="filters">
            {/* Tournament Dropdown */}
            <div className="filter-dropdown" ref={tournamentDropdownRef}>
              <button
                className="chip"
                onClick={() => {
                  setShowTournamentDropdown(!showTournamentDropdown);
                  setShowTeamDropdown(false);
                }}
              >
                {selectedTournamentName}
                <span className={`caret ${showTournamentDropdown ? 'open' : ''}`}>
                  <img src={Images.subheader_dropdown} alt="Dropdown" />
                </span>
              </button>
              {showTournamentDropdown && (
                <div className="dropdown-menu">
                  <div className="searchbox">
                    <input
                      type="text"
                      placeholder="Search Tournaments..."
                      value={tournamentSearch}
                      onChange={(e) => setTournamentSearch(e.target.value)}
                    />
                    <img src={Images.dark_search_icon} alt="Search" />
                  </div>
                  <div className="dropdown-list">
                    {filteredTournaments.length > 0 ? (
                      filteredTournaments.map((tournament) => (
                        <div
                          key={tournament.tournamentId}
                          className="dropdown-item"
                          onClick={() => {
                            setSelectedTournamentId(tournament.tournamentId);
                            setShowTournamentDropdown(false);
                            setTournamentSearch("");
                          }}
                        >
                          {tournament.tournamentName}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-empty">No tournaments found</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Team Dropdown */}
            <div className="filter-dropdown" ref={teamDropdownRef}>
              <button
                className="chip"
                onClick={() => {
                  setShowTeamDropdown(!showTeamDropdown);
                  setShowTournamentDropdown(false);
                }}
              >
                {selectedTeamName}
                <span className={`caret ${showTeamDropdown ? 'open' : ''}`}>
                  <img src={Images.subheader_dropdown} alt="Dropdown" />
                </span>
              </button>
              {showTeamDropdown && (
                <div className="dropdown-menu">
                  <div className="searchbox">
                    <input
                      type="text"
                      placeholder="Search Teams..."
                      value={teamSearch}
                      onChange={(e) => setTeamSearch(e.target.value)}
                    />
                    <img src={Images.dark_search_icon} alt="Search" />
                  </div>
                  <div className="dropdown-list">
                    <div
                      className="dropdown-item"
                      onClick={() => {
                        setSelectedTeamSnapshotId(null);
                        setShowTeamDropdown(false);
                        setTeamSearch("");
                      }}
                    >
                      All Teams
                    </div>
                    {filteredTeams.length > 0 ? (
                      filteredTeams.map((team) => (
                        <div
                          key={team.teamSnapshotId}
                          className="dropdown-item"
                          onClick={() => {
                            setSelectedTeamSnapshotId(team.teamSnapshotId);
                            setShowTeamDropdown(false);
                            setTeamSearch("");
                          }}
                        >
                          {team.teamName}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-empty">No teams found</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Search Box */}
            <div className="searchbox" ref={searchBoxRef}>
              <input
                type="text"
                placeholder="Search Players..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <img src={Images.dark_search_icon} alt="Search" />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="squads-loading">Loading...</div>
        ) : !filteredPlayers || filteredPlayers.length === 0 ? (
          <NoDataFound message="NO PLAYERS FOUND" />
        ) : (
          <div className="player-card-wrap">
            {filteredPlayers.map((player) => (
              <PlayerCard key={player.playerId} player={player} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Squads;
